import React, { useState, useEffect, useMemo } from "react"
import api from "../../utils/api"
import Icon from "../Icon"
import { getGroupTheme } from "../../utils/groupTheme"
import { getApiErrorMessage } from "../../utils/errorMessage"

const sanitizePhoneInput = (value = "") => {
  const raw = String(value || "")
  let cleaned = raw.replace(/[^\d+]/g, "")
  if (cleaned.includes("+")) {
    cleaned = cleaned.startsWith("+")
      ? `+${cleaned.slice(1).replace(/\+/g, "")}`
      : cleaned.replace(/\+/g, "")
  }
  return cleaned
}

const toDigits = (value = "") => String(value || "").replace(/\D/g, "")

const getPhoneVariants = (value = "") => {
  const input = String(value || "").trim()
  const digits = toDigits(input)
  if (!digits) return []

  const variants = new Set()
  const add = (v) => {
    if (!v) return
    variants.add(String(v))
  }

  add(digits)

  // Kenya-friendly normalization:
  // 07XXXXXXXX -> 2547XXXXXXXX
  // 7XXXXXXXX  -> 2547XXXXXXXX
  // 2547XXXXXXX stays as is
  if (digits.length === 10 && digits.startsWith("0")) {
    const tail = digits.slice(1)
    add(`254${tail}`)
    add(`+254${tail}`)
  } else if (digits.length === 9 && digits.startsWith("7")) {
    add(`0${digits}`)
    add(`254${digits}`)
    add(`+254${digits}`)
  } else if (digits.length === 12 && digits.startsWith("254")) {
    const tail = digits.slice(3)
    add(`0${tail}`)
    add(`+${digits}`)
  }

  return Array.from(variants)
}

const normalizeId = (value) => String(value || "").trim()

const getPayScoreStatusClasses = (status) => {
  const normalized = String(status || "").toLowerCase()
  if (normalized === "excellent") return "bg-emerald-50 text-emerald-600 border-emerald-100"
  if (normalized === "good") return "bg-green-50 text-green-600 border-green-100"
  if (normalized === "fair") return "bg-amber-50 text-amber-600 border-amber-100"
  if (normalized === "risk") return "bg-orange-50 text-orange-600 border-orange-100"
  if (normalized === "restricted") return "bg-red-50 text-red-600 border-red-100"
  return "bg-gray-50 text-gray-500 border-gray-100"
}

const formatPayScorePercent = (value) =>
  Number.isFinite(Number(value)) ? `${Number(value).toFixed(1)}%` : "--"

function GroupInviteModal({ open, onClose, group, onSent }) {
  const theme = getGroupTheme(group)
  const [searchTerm, setSearchTerm] = useState("")
  const [searchLoading, setSearchLoading] = useState(false)
  const [searchError, setSearchError] = useState("")
  const [results, setResults] = useState([])
  const [userPayScoreById, setUserPayScoreById] = useState({})
  const [userPayScoreLoadingById, setUserPayScoreLoadingById] = useState({})

  const [recipients, setRecipients] = useState([])
  const [message, setMessage] = useState("")
  const [sendLoading, setSendLoading] = useState(false)
  const [sendError, setSendError] = useState("")
  const [sendSummary, setSendSummary] = useState(null)

  const groupId = group?.id
  const existingMemberIds = useMemo(() => {
    const ids = new Set()
    const members = Array.isArray(group?.members) ? group.members : []
    for (const member of members) {
      const id = member?.id || member?.user?._id || member?.user
      if (id) ids.add(String(id))
    }
    return ids
  }, [group?.members])

  const fetchUsersPayScores = async (usersList = []) => {
    const userIds = [...new Set(
      (Array.isArray(usersList) ? usersList : [])
        .map((entry) => normalizeId(entry?._id))
        .filter(Boolean)
    )]

    const idsToLoad = userIds.filter(
      (id) => !(id in userPayScoreById) && !userPayScoreLoadingById[id]
    )
    if (idsToLoad.length === 0) return

    setUserPayScoreLoadingById((prev) => {
      const next = { ...prev }
      idsToLoad.forEach((id) => {
        next[id] = true
      })
      return next
    })

    try {
      const response = await api.get("/payscore/users", {
        params: { ids: idsToLoad.join(",") },
      })
      const scores = response?.data?.scores || {}

      setUserPayScoreById((prev) => {
        const next = { ...prev }
        idsToLoad.forEach((id) => {
          next[id] = scores[id] || null
        })
        return next
      })
    } catch (error) {
      console.error("Error loading user PaySCOREs for invites:", error)
      setUserPayScoreById((prev) => {
        const next = { ...prev }
        idsToLoad.forEach((id) => {
          if (!(id in next)) next[id] = null
        })
        return next
      })
    } finally {
      setUserPayScoreLoadingById((prev) => {
        const next = { ...prev }
        idsToLoad.forEach((id) => {
          delete next[id]
        })
        return next
      })
    }
  }

  const renderInvitePayScoreBadges = (userId) => {
    const normalizedUserId = normalizeId(userId)
    if (!normalizedUserId) return null

    if (userPayScoreLoadingById[normalizedUserId]) {
      return (
        <span className="text-[10px] text-gray-400 animate-pulse font-medium">
          Loading scores...
        </span>
      )
    }

    const summary = userPayScoreById[normalizedUserId]
    if (!summary) return null

    const badges = [
      {
        key: "rl",
        label: "RL",
        value: summary?.final?.score,
        status: summary?.final?.status,
      },
      {
        key: "groups",
        label: "Groups",
        value: summary?.modules?.group?.score,
        status: summary?.modules?.group?.status,
      },
    ]

    return (
      <div className="flex items-center gap-1">
        {badges.map((badge) => (
          <div
            key={`${normalizedUserId}-${badge.key}`}
            className={`flex items-center gap-1 px-1 py-0.5 rounded-md text-[10px] font-bold border leading-none ${getPayScoreStatusClasses(
              badge.status
            )}`}
            title={`${badge.label} PaySCORE: ${formatPayScorePercent(badge.value)} (${badge.status || "N/A"})`}
          >
            <span className="opacity-70 uppercase">{badge.label}</span>
            <span>{formatPayScorePercent(badge.value)}</span>
          </div>
        ))}
      </div>
    )
  }

  // Debounced Auto-Search
  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      const sanitizedInput = sanitizePhoneInput(searchTerm)
      const inputVariants = getPhoneVariants(sanitizedInput)

      if (sanitizedInput && inputVariants.length > 0) {
        setSearchLoading(true)
        setSearchError("")
        setResults([])
        try {
          // Query backend using canonical digits variant; enforce exact match locally.
          const canonicalQuery =
            inputVariants.find((v) => /^\d+$/.test(v) && v.startsWith("254")) ||
            inputVariants.find((v) => /^\d+$/.test(v)) ||
            toDigits(sanitizedInput)
          const res = await api.get(`/auth/search-users?phone=${encodeURIComponent(canonicalQuery)}`)
          const users = Array.isArray(res?.data?.users) ? res.data.users : []
          const normalizedInputSet = new Set(inputVariants.map((v) => toDigits(v)))
          const exactUsers = users.filter((u) => {
            const userDigits = toDigits(u?.phone || "")
            if (!userDigits) return false
            const userVariants = getPhoneVariants(userDigits).map((v) => toDigits(v))
            return userVariants.some((v) => normalizedInputSet.has(v))
          })
          setResults(exactUsers)
          fetchUsersPayScores(exactUsers)
        } catch (err) {
          // Only show error if it's not a 404 "not found" which is common while typing
          if (err?.response?.status !== 404) {
            const msg = getApiErrorMessage(err, "Search failed.")
            setSearchError(msg)
          }
        } finally {
          setSearchLoading(false)
        }
      } else {
        setResults([])
        setSearchError("")
      }
    }, 500) // 500ms debounce

    return () => clearTimeout(delayDebounceFn)
    // fetchUsersPayScores intentionally omitted: this effect only debounces on phone input changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm])

  if (!open) return null

  const reset = () => {
    setSearchTerm("")
    setSearchError("")
    setResults([])
    setRecipients([])
    setMessage("")
    setSendError("")
    setSendSummary(null)
    setUserPayScoreById({})
    setUserPayScoreLoadingById({})
  }

  const close = () => {
    reset()
    onClose?.()
  }

  const addRecipient = (u) => {
    if (!u?._id) return
    if (existingMemberIds.has(String(u._id))) return
    setRecipients((prev) => {
      if (prev.some((p) => String(p._id) === String(u._id))) return prev
      return [u, ...prev]
    })
    fetchUsersPayScores([u])
    // Optional: Clear search after adding (or keep it if they want to verify)
    // setSearchTerm("") 
  }

  const removeRecipient = (userId) => {
    setRecipients((prev) => prev.filter((p) => String(p._id) !== String(userId)))
  }

  const sendInvites = async () => {
    if (!groupId) return
    if (recipients.length === 0) {
      setSendError("Add at least one recipient.")
      return
    }
    setSendLoading(true)
    setSendError("")
    setSendSummary(null)
    try {
      const payload = {
        recipients: recipients.map((r) => ({ userId: r._id })),
        message: message || ""
      }
      const res = await api.post(`/groups/${groupId}/invites`, payload)
      const summary = {
        created: Array.isArray(res?.data?.created) ? res.data.created.length : 0,
        skipped: Array.isArray(res?.data?.skipped) ? res.data.skipped.length : 0,
        notFound: Array.isArray(res?.data?.notFound) ? res.data.notFound.length : 0
      }
      setSendSummary(summary)
      onSent?.(summary)

      // Clear recipients on success so they don't resend immediately
      if (summary.created > 0) {
        setRecipients([])
        setMessage("")
      }

    } catch (err) {
      const msg = getApiErrorMessage(err, "Failed to send invitations.")
      setSendError(msg)
    } finally {
      setSendLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[130] flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-lg rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">

        {/* Header */}
        <div className={`bg-gradient-to-r ${theme.bg}/30 to-white px-6 py-5 border-b border-gray-100 flex items-center justify-between`}>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl ${theme.bg} ${theme.text} flex items-center justify-center shadow-sm border ${theme.border}`}>
              <Icon name="user-plus" size="1.2rem" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 leading-tight">Send Invitations</h2>
              <p className="text-xs text-gray-500 font-medium mt-0.5">
                Add members to <span className={`font-medium ${theme.text}`}>{group?.name}</span>
              </p>
            </div>
          </div>
          <button
            onClick={close}
            className="p-2 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <Icon name="x" size="1.2rem" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">

          {/* Notifications / Status */}
          {!!sendSummary && (
            <div className={`p-4 rounded-2xl border ${sendSummary.created > 0 ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
              <div className="flex items-start gap-3">
                <div className={`mt-0.5 p-1 rounded-full ${sendSummary.created > 0 ? 'bg-green-100 text-green-600' : 'bg-amber-100 text-amber-600'}`}>
                  <Icon name={sendSummary.created > 0 ? "check" : "info-circle"} size="1rem" />
                </div>
                <div>
                  <h4 className={`text-sm font-bold ${sendSummary.created > 0 ? 'text-green-800' : 'text-amber-800'}`}>
                    {sendSummary.created > 0 ? 'Invitations Sent!' : 'No Invitations Sent'}
                  </h4>
                  <p className="text-xs mt-1 text-gray-600">
                    Successfully sent to <b>{sendSummary.created}</b> users.
                    {sendSummary.skipped > 0 && <span> (Skipped {sendSummary.skipped} duplicates)</span>}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Search Section */}
          <div className="space-y-3">
            <label className="block text-sm font-semibold text-gray-700">
              Find User by Phone
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                {searchLoading ? (
                  <div className={`animate-spin h-4 w-4 border-2 border-${theme.primary} border-t-transparent rounded-full`}></div>
                ) : (
                  <Icon name="search" size="1.1rem" className="text-gray-400" />
                )}
              </div>
              <input
                value={searchTerm}
                onChange={(e) => setSearchTerm(sanitizePhoneInput(e.target.value))}
                className={`w-full pl-11 pr-4 py-3.5 bg-gray-50 border border-gray-200 rounded-2xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 ${theme.ring}/20 focus:border-${theme.primary} transition-all`}
                placeholder="Enter full phone number..."
                autoFocus
              />
            </div>

            {/* Search Results */}
            {searchTerm.length > 0 && !searchLoading && results.length === 0 && (
              <p className="text-xs text-center text-gray-400 py-2">No users found matching "{searchTerm}"</p>
            )}

            {results.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {results.map((u) => {
                  const isAdded = recipients.some((r) => String(r._id) === String(u._id))
                  const isAlreadyMember = existingMemberIds.has(String(u._id))
                  const disableAdd = isAdded || isAlreadyMember
                  return (
                    <div key={u._id} className="bg-white border border-gray-100 rounded-2xl px-3 py-2 hover:bg-gray-50/80 transition-all shadow-sm flex items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-col mb-0.5">
                          <p className="text-[15px] font-bold text-gray-900 leading-tight mb-0.5">{u.name}</p>
                          <div className="flex flex-wrap gap-1">
                            {renderInvitePayScoreBadges(u._id)}
                          </div>
                        </div>
                        <p className="text-[11px] text-gray-500 font-medium">
                          <span className="opacity-60">Phone:</span> {u.phone || "-"}
                        </p>
                      </div>
                      <div className="flex-shrink-0">
                        <button
                          onClick={() => !disableAdd && addRecipient(u)}
                          disabled={disableAdd}
                          className={`px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all ${disableAdd
                              ? "bg-gray-100 text-gray-400 cursor-default"
                              : `${theme.bg} ${theme.text} hover:shadow-md active:scale-95 border ${theme.border}`
                            }`}
                        >
                          {isAlreadyMember ? "Member" : isAdded ? "Added" : "Add"}
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
            {!!searchError && <p className="text-xs text-red-500 px-1">{searchError}</p>}
          </div>

          {/* Recipients List */}
          {recipients.length > 0 && (
            <div className="bg-gray-50 rounded-2xl p-4 border border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500">Selected Recipients</h3>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${theme.badge}`}>{recipients.length}</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {recipients.map((u) => (
                  <div key={u._id} className="flex items-center gap-2 bg-white border border-gray-200 pl-3 pr-2 py-1.5 rounded-full shadow-sm">
                    <span className="text-xs font-semibold text-gray-700">{u.name}</span>
                    <button onClick={() => removeRecipient(u._id)} className="text-gray-400 hover:text-red-500 transition-colors">
                      <Icon name="x-circle" size="1rem" className="fill-current" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Message Input */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Message (Optional)</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              className={`w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl text-sm focus:outline-none focus:ring-2 ${theme.ring}/20 focus:border-${theme.primary} transition-all resize-none`}
              placeholder="Write a specialized message..."
            />
          </div>

          {!!sendError && (
            <div className="p-3 bg-red-50 border border-red-100 rounded-xl flex items-center gap-2 text-red-600 text-sm">
              <Icon name="alert-circle" size="1.2rem" />
              {sendError}
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/50 flex items-center justify-end gap-3">
          <button
            onClick={close}
            className="px-5 py-2.5 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={sendInvites}
            disabled={sendLoading || recipients.length === 0}
            className={`px-6 py-2.5 rounded-xl text-sm font-semibold text-white transition-all flex items-center gap-2 ${sendLoading || recipients.length === 0
                ? "bg-gray-300 shadow-none cursor-not-allowed"
                : `${theme.button} hover:-translate-y-0.5 shadow-lg`
              }`}
          >
            {sendLoading ? (
              <>
                <div className="animate-spin h-4 w-4 border-2 border-white/30 border-t-white rounded-full"></div>
                Sending...
              </>
            ) : (
              <>
                <Icon name="send" size="1rem" />
                Send Invites
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default GroupInviteModal
